import React, {
  useRef,
  useLayoutEffect,
  useState,
  type FC,
  useEffect,
  Ref
} from "react";
import { ScrollContainer, ShadowWrapper } from "./styled-components";
import type { ScrollProps, BorderToShow } from "./types";

export const Scroll: FC<ScrollProps> = ({
  children,
  isDark = false,
  qaLabel = "scroll",
  showBorderTop = false,
  showBorderRight = false,
  showBorderBottom = false,
  showBorderLeft = false,
  hasShadow = false,
  component = "div",
  ...props
}) => {
  const [scrollPosition, setScrollPosition] = useState("top");
  const [hideShadow, setHideShadow] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const borderToShow: BorderToShow = {
    top: showBorderTop,
    right: showBorderRight,
    bottom: showBorderBottom,
    left: showBorderLeft
  };

  useLayoutEffect(() => {
    if (hasShadow && scrollRef.current) {
      setHideShadow(
        scrollRef.current?.clientHeight === scrollRef.current?.scrollHeight
      );
      scrollRef.current?.addEventListener("scroll", handleScroll);

      return () => {
        scrollRef.current?.removeEventListener("scroll", handleScroll);
      };
    }

    return () => scrollRef.current?.removeEventListener("scroll", handleScroll);
  }, [scrollRef, hasShadow]);

  const handleScroll = () => {
    if (scrollRef.current) {
      if (props.onImperativeScroll) {
        props.onImperativeScroll(
          scrollRef.current as unknown as Ref<HTMLDivElement | null>
        );
      }

      if (scrollRef.current?.scrollTop === 0) {
        return setScrollPosition("top");
      }

      const differenceScroll =
        scrollRef.current?.scrollTop +
        scrollRef.current?.clientHeight -
        scrollRef.current?.scrollHeight;

      if (differenceScroll > -3 && differenceScroll < 3) {
        return setScrollPosition("bottom");
      }

      return scrollPosition !== "middle" && setScrollPosition("middle");
    }

    return null;
  };

  useEffect(() => {
    if (props.onRender && scrollRef) {
      props.onRender(scrollRef);
    }
  }, [props?.onRender, scrollRef]);

  return hasShadow ? (
    <ShadowWrapper
      data-scroll-position={scrollPosition}
      data-hide-shadow={hideShadow}
      data-qa-label={`${qaLabel}-shadow`}
      {...props}
    >
      <ScrollContainer
        ref={scrollRef}
        data-mode={isDark ? "dark" : "light"}
        data-qa-label={qaLabel}
        borderToShow={borderToShow}
        hasShadow={hasShadow}
        as={component}
        {...props}
      >
        {children}
      </ScrollContainer>
    </ShadowWrapper>
  ) : (
    <ScrollContainer
      ref={scrollRef}
      data-mode={isDark ? "dark" : "light"}
      data-qa-label={qaLabel}
      borderToShow={borderToShow}
      hasShadow={hasShadow}
      as={component}
      {...props}
    >
      {children}
    </ScrollContainer>
  );
};

export type { ScrollProps };
